import React, { useEffect, useRef, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { rememberReturnPath, signupPathFor, takeReturnPath } from '../../context/signedInState';
import { signInTrouble } from '../../context/sessionErrors';
import { usePersonCheck } from './PersonCheck';
import { useAuthPage } from '../../hooks/useAuthPage';
import { PasswordInput } from '../common/PasswordInput';
import { FlowHeader } from './TinycmsSignupFlow';

const LOGIN_MUTATION = gql`
  mutation Login(
    $email: String!
    $password: String!
    $keepSignedIn: Boolean
    $personCheck: String
  ) {
    login(
      email: $email
      password: $password
      keepSignedIn: $keepSignedIn
      personCheck: $personCheck
    )
  }
`;

export const REQUEST_EMAIL_CODE = gql`
  mutation RequestEmailCode($email: String!, $purpose: String, $personCheck: String) {
    requestEmailCode(email: $email, purpose: $purpose, personCheck: $personCheck)
  }
`;

const SIGN_IN_WITH_CODE = gql`
  mutation SignInWithEmailCode(
    $email: String!
    $code: String!
    $keepSignedIn: Boolean
    $personCheck: String
  ) {
    signInWithEmailCode(
      email: $email
      code: $code
      keepSignedIn: $keepSignedIn
      personCheck: $personCheck
    )
  }
`;

export const FINISH_CHECK = 'Finish the check first.';

/** Seconds before "Send a new code" works again. */
export const RESEND_SECONDS = 30;

/**
 * tinycms sign-in ("tinycms UX optimization review v3.zip", Signup Journey):
 * one column, the same header as signup, and a way into signup for someone
 * new. Session notices (signed out, session expired) come from the same
 * places the shared Login reads them.
 *
 * Two ways in, side by side: email + password, or "Email me a code" (a
 * 6-digit code by email; no password needed). An account made without a
 * password signs in with codes only.
 */
export function TinycmsLogin() {
  useAuthPage();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading } = useAuth();
  // Email codes by default; a password is one link away (owner, 2026-10-15).
  const [mode, setMode] = useState<'code' | 'password'>('code');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [hashing, setHashing] = useState(false);
  // Off unless asked for: 30 days from the last use instead of 24 hours (backlog 266aecb0).
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const check = usePersonCheck();

  // Once only: signing in calls this, and so does the signed-in check right
  // after; the second call found the page already taken and went to Today.
  const redirected = useRef(false);
  const redirectAfterAuth = () => {
    if (redirected.current) return;
    redirected.current = true;
    navigate(takeReturnPath(window.location.search) || '/');
  };

  useEffect(() => {
    if (!isLoading && isAuthenticated) redirectAfterAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  // Messages left by logout(), the 401 handler and ErrorBoundary win; otherwise
  // say something only when a specific page turned the person away.
  useEffect(() => {
    const authError = sessionStorage.getItem('authError');
    if (authError) {
      sessionStorage.removeItem('authError');
      setNotice(
        /all devices/i.test(authError)
          ? { tone: 'ok', text: 'You’ve been signed out of all devices.' }
          : /logged out|signed out/i.test(authError)
            ? { tone: 'ok', text: 'You’ve been signed out.' }
            : { tone: 'error', text: authError }
      );
    }
    const from = (location.state as { from?: string } | null)?.from;
    if (from && from !== '/') {
      // A reason left above says more than this one, however often this runs.
      setNotice((shown) => shown ?? { tone: 'error', text: 'Sign in to see that page.' });
      // Back there after signing in (an approval link, an article…): before,
      // the page was only named here, and sign-in always landed on Today.
      rememberReturnPath(from);
    }
    if (from) navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [loginMutation, { loading }] = useMutation<{ login: string }>(LOGIN_MUTATION, {
    onCompleted: () => {
      login();
      redirectAfterAuth();
    },
  });

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const [requestCode, { loading: requesting }] = useMutation(REQUEST_EMAIL_CODE);
  const [signInWithCode, { loading: verifying }] = useMutation<{ signInWithEmailCode: string }>(
    SIGN_IN_WITH_CODE
  );

  const message = (err: unknown, fallback: string) =>
    signInTrouble(err) || (err instanceof Error && err.message ? err.message : fallback);

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) {
      setFormError('Enter your email.');
      return;
    }
    if (check.waiting) {
      setFormError(FINISH_CHECK);
      return;
    }
    setFormError('');
    try {
      await requestCode({ variables: { email, purpose: 'sign_in', ...check.variables } });
      check.passed();
      setMode('code');
      setCodeSent(true);
      setCode('');
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      check.failed(err);
      setFormError(message(err, 'The code couldn’t be sent. Try again.'));
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.replace(/\D/g, '').length !== 6) {
      setFormError('Enter the 6-digit code from the email.');
      return;
    }
    if (check.waiting) {
      setFormError(FINISH_CHECK);
      return;
    }
    setFormError('');
    try {
      await signInWithCode({ variables: { email, code, keepSignedIn, ...check.variables } });
      login();
      redirectAfterAuth();
    } catch (err) {
      check.failed(err);
      setFormError(message(err, 'Sign in failed. Try again.'));
    }
  };

  const switchMode = (next: 'code' | 'password') => {
    setMode(next);
    setCodeSent(false);
    setCode('');
    setFormError('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email) || !password) {
      setFormError('Enter your email and password, or choose Email me a code.');
      return;
    }
    if (check.waiting) {
      setFormError(FINISH_CHECK);
      return;
    }
    setFormError('');
    setHashing(true);
    try {
      await loginMutation({ variables: { email, password, keepSignedIn, ...check.variables } });
    } catch (err) {
      check.failed(err);
      setFormError(message(err, 'Sign in failed. Try again.'));
    } finally {
      setHashing(false);
    }
  };

  const busy = loading || hashing;
  return (
    <div className="flow">
      {/* No header link: someone signing in isn't shopping, and "Create an
          account" already sits under the form. */}
      <FlowHeader />
      <main className="flow-main" data-gate="true">
        <form
          className="flow-signin"
          onSubmit={mode === 'password' ? submit : codeSent ? submitCode : sendCode}
          noValidate
        >
          {notice ? (
            <div className="flow-notice" data-tone={notice.tone}>
              {notice.tone === 'ok' ? <span className="flow-status-dot" /> : null}
              {notice.text}
            </div>
          ) : null}
          <h1 className="flow-h1">Sign in</h1>
          <p className="flow-lede">
            {mode === 'code' && codeSent
              ? `If there’s a tinycms account for ${email}, a 6-digit code is on its way. It works for 10 minutes.`
              : 'Pick up where your agents left off.'}
          </p>
          <div className="flow-fields">
            {mode === 'code' && codeSent ? (
              <label className="flow-field">
                <span className="flow-label">Code</span>
                <input
                  className="flow-input flow-code"
                  value={code}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="XXXXXX"
                  autoFocus
                  onChange={(e) => {
                    // Digits only, the first six: a pasted "196 391" or "Code: 196391" still works.
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setFormError('');
                  }}
                />
              </label>
            ) : (
              <label className="flow-field">
                <span className="flow-label">Email</span>
                {/* iOS Password AutoFill fills the saved email and password
                    together only from a field marked "username", and reads the
                    form when a field gains focus. key={mode}: switching to a
                    password builds this field anew, so it reads the form again
                    with the password field in it (otherwise it keeps focus from
                    the code view and fills the email alone). */}
                <input
                  key={mode}
                  id="signin-email"
                  name="email"
                  className="flow-input"
                  type="email"
                  value={email}
                  placeholder="Email"
                  autoComplete="username"
                  autoFocus={mode === 'code' || !email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setFormError('');
                  }}
                />
              </label>
            )}
            {mode === 'password' ? (
              // htmlFor: the label also holds buttons, so name the field it's for.
              <label className="flow-field" htmlFor="signin-password">
                <span className="flow-label flow-label-row">
                  Password
                  <button
                    type="button"
                    className="flow-link"
                    onClick={() => {
                      // Forgot it: straight to a code, sent now if the email is in.
                      switchMode('code');
                      if (/\S+@\S+\.\S+/.test(email)) void sendCode();
                    }}
                  >
                    Forgot it? Use a code
                  </button>
                </span>
                <PasswordInput
                  id="signin-password"
                  name="password"
                  className="flow-input"
                  value={password}
                  placeholder="Password"
                  autoComplete="current-password"
                  // The email is already in: straight to the password.
                  autoFocus={Boolean(email)}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setFormError('');
                  }}
                />
              </label>
            ) : null}
          </div>
          <label className="flow-keep">
            <input
              type="checkbox"
              checked={keepSignedIn}
              onChange={(e) => setKeepSignedIn(e.target.checked)}
            />
            <span>
              Keep me signed in on this device
              <span className="flow-keep-note">
                For 30 days after you last use it. Leave this off on a shared computer.
              </span>
            </span>
          </label>
          {check.widget}
          {formError ? <p className="flow-error flow-submit-error">{formError}</p> : null}
          {mode === 'password' ? (
            <>
              <button type="submit" className="flow-btn" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <p className="flow-fine flow-center">
                <button
                  type="button"
                  className="flow-link flow-link-quiet"
                  onClick={() => switchMode('code')}
                >
                  Email me a code instead
                </button>
              </p>
            </>
          ) : codeSent ? (
            <>
              <button type="submit" className="flow-btn" disabled={verifying}>
                {verifying ? 'Signing in…' : 'Sign in'}
              </button>
              <div className="flow-code-actions">
                <button
                  type="button"
                  className="flow-link"
                  disabled={cooldown > 0 || requesting}
                  onClick={() => void sendCode()}
                >
                  {cooldown > 0 ? `Send a new code in ${cooldown}s` : 'Send a new code'}
                </button>
                <button type="button" className="flow-link" onClick={() => switchMode('code')}>
                  Use a different email
                </button>
              </div>
            </>
          ) : (
            <>
              <button type="submit" className="flow-btn" disabled={requesting}>
                {requesting ? 'Sending…' : 'Email me a code'}
              </button>
              <p className="flow-fine flow-center">
                <button
                  type="button"
                  className="flow-link flow-link-quiet"
                  onClick={() => switchMode('password')}
                >
                  Use a password instead
                </button>
              </p>
            </>
          )}
          <div className="flow-signin-foot">
            New to tinycms?{' '}
            <button
              type="button"
              className="flow-link"
              onClick={() =>
                navigate(signupPathFor(new URLSearchParams(location.search).get('next')))
              }
            >
              Create an account
            </button>
            .
          </div>
        </form>
      </main>
    </div>
  );
}
